import { useSyncExternalStore } from 'react';
import { safeLocalStorage } from './browser';

/**
 * Other tabs (MAP 225a, item 9).
 *
 * Every tab of this origin shares one cookie jar and one IndexedDB. When one tab signs in as
 * somebody else, signs out, or switches shop, every other tab is still drawing the old
 * account's screens, and anything it sends from then on goes out under the NEW cookie.
 *
 * So each tab tells the others, two ways at once because neither is enough alone:
 *
 * - a `BroadcastChannel('servwell-identity')`, which is immediate;
 * - a write to one localStorage key, whose `storage` event reaches the other tabs where the
 *   channel is missing or unreliable (Safari, an iOS home-screen app). The same key is read
 *   at start, so a tab opened in the middle of another tab's sign-in knows it.
 *
 * And it tells them at two moments:
 *
 * - **a transition**, BEFORE the sign-in, sign-up, join, sign-out or set-active call leaves,
 *   so a stale tab stops before the cookie changes underneath it rather than after;
 * - **the account**, once a change of account has been confirmed by the network and written
 *   to disk (or, at sign-out, nobody).
 *
 * What a tab does with that depends on whether it is DRAWING an account's data (told by the
 * gate, `drawing`), never on the label its restored cache happened to carry:
 *
 * - drawing, and it hears a transition or another account: it STOPS for good ("You signed in
 *   as someone else in another tab", Reload);
 * - drawing, and it hears its own account (the same person and shop, perhaps in a new role):
 *   it asks the server again, and its role is checked like any other answer;
 * - drawing nothing, and it hears a transition (or starts during one): it is HELD, drawing no
 *   shop, until an account is announced or the transition is older than
 *   `TRANSITION_HOLD_MS`, then asks the server again;
 * - drawing nothing, and it hears an account: it asks the server again.
 *
 * A stopped tab sends nothing more (MAP 225b): `onStop` stops its queued writes, so the write
 * guard link refuses every send from it, paused writes leave its memory while their rows stay
 * for their owner, and a write in back-off is parked at its next attempt.
 */

export const IDENTITY_CHANNEL = 'servwell-identity';
/** The localStorage key: the latest message, so its `storage` event is the message. */
export const IDENTITY_STORAGE_KEY = 'servwell-identity';
/** The longest another tab's identity call holds this one. */
export const TRANSITION_HOLD_MS = 30_000;

export type IdentityMessage =
  | { kind: 'transition'; tab: string; nonce: string; at: number }
  | { kind: 'account'; tab: string; nonce: string; at: number; account: string | null };

/** Why a tab stopped. `signed-out` only when the other tab named nobody. */
export type Stopped = 'other-account' | 'signed-out';

export type TabsSnapshot = Readonly<{ stopped: Stopped | null; held: boolean }>;

export interface ChannelLike {
  postMessage(message: unknown): void;
  addEventListener(type: 'message', listener: (event: { data: unknown }) => void): void;
  removeEventListener(
    type: 'message',
    listener: (event: { data: unknown }) => void,
  ): void;
}

export type MessageStorage = Pick<Storage, 'getItem' | 'setItem'>;

export interface Timers {
  now(): number;
  setTimeout(callback: () => void, ms: number): unknown;
  clearTimeout(handle: unknown): void;
}

/** How messages travel between tabs: injectable so both routes are testable in Node. */
export interface TabRoutes {
  /** Opens the channel; undefined where there is no BroadcastChannel. */
  channel: (() => ChannelLike) | undefined;
  storage: MessageStorage | undefined;
  /** Subscribe to `storage` events: called with each event's key and new value. */
  listenStorage: (
    listener: (key: string | null, value: string | null) => void,
  ) => () => void;
}

export interface IdentityTabsOptions extends TabRoutes {
  /** This tab just stopped: drop what it was showing and never write the blob again. */
  onStop: () => void;
  /** The account may have changed under this tab: ask the server again. */
  onRecheck: () => void;
  timers?: Timers;
}

const systemTimers: Timers = {
  now: () => Date.now(),
  setTimeout: (callback, ms) => {
    const handle = setTimeout(callback, ms);
    // Node only, so a test process is not held open by a hold.
    (handle as unknown as { unref?: () => void }).unref?.();
    return handle;
  },
  clearTimeout: (handle) => clearTimeout(handle as ReturnType<typeof setTimeout>),
};

const randomId = (): string =>
  globalThis.crypto?.randomUUID?.() ??
  `${Date.now()}-${Math.random().toString(36).slice(2)}`;

function isMessage(raw: unknown): raw is IdentityMessage {
  if (typeof raw !== 'object' || raw === null) return false;
  const m = raw as Record<string, unknown>;
  if (typeof m.tab !== 'string' || typeof m.at !== 'number') return false;
  if (m.kind === 'transition') return true;
  return m.kind === 'account' && (m.account === null || typeof m.account === 'string');
}

function parseMessage(value: string | null | undefined): IdentityMessage | null {
  if (!value) return null;
  try {
    const raw: unknown = JSON.parse(value);
    return isMessage(raw) ? raw : null;
  } catch {
    return null;
  }
}

export class IdentityTabs {
  readonly tab = randomId();
  readonly #opts: IdentityTabsOptions;
  readonly #timers: Timers;
  #channel: ChannelLike | null = null;
  #drawing: string | null = null;
  #holdTimer: unknown = null;
  #snapshot: TabsSnapshot = { stopped: null, held: false };
  readonly #listeners = new Set<() => void>();

  constructor(opts: IdentityTabsOptions) {
    this.#opts = opts;
    this.#timers = opts.timers ?? systemTimers;
    // At start, before anything is drawn: is another tab in the middle of changing account?
    let latest: IdentityMessage | null = null;
    try {
      latest = parseMessage(opts.storage?.getItem(IDENTITY_STORAGE_KEY));
    } catch {
      latest = null;
    }
    if (latest?.kind === 'transition') this.#hold(latest.at);
  }

  snapshot = (): TabsSnapshot => this.#snapshot;

  subscribe = (listener: () => void): (() => void) => {
    this.#listeners.add(listener);
    return () => {
      this.#listeners.delete(listener);
    };
  };

  #set(next: TabsSnapshot): void {
    this.#snapshot = next;
    for (const listener of this.#listeners) listener();
  }

  /** The gate says whose data this tab is drawing now (`accountKey`), or null for nobody's. */
  drawing(account: string | null): void {
    this.#drawing = account;
  }

  #open(): ChannelLike | null {
    if (this.#channel) return this.#channel;
    try {
      this.#channel = this.#opts.channel?.() ?? null;
    } catch {
      this.#channel = null;
    }
    return this.#channel;
  }

  listen(): () => void {
    const onMessage = (event: { data: unknown }) => this.receive(event.data);
    const channel = this.#open();
    channel?.addEventListener('message', onMessage);
    const unlistenStorage = this.#opts.listenStorage((key, value) => {
      if (key === IDENTITY_STORAGE_KEY) this.receive(parseMessage(value));
    });
    return () => {
      channel?.removeEventListener('message', onMessage);
      unlistenStorage();
    };
  }

  /** Called BEFORE any call that can change who the cookie belongs to. */
  beginTransition(): void {
    this.#send({
      kind: 'transition',
      tab: this.tab,
      nonce: randomId(),
      at: this.#timers.now(),
    });
  }

  /** A change of account reached the disk in this tab (null: signed out). */
  announce(account: string | null): void {
    this.#send({
      kind: 'account',
      tab: this.tab,
      nonce: randomId(),
      at: this.#timers.now(),
      account,
    });
  }

  #send(message: IdentityMessage): void {
    let routes = 0;
    try {
      const channel = this.#open();
      if (channel) {
        channel.postMessage(message);
        routes += 1;
      }
    } catch {
      // A closed channel: the storage route below may still carry it.
    }
    try {
      if (this.#opts.storage) {
        // The nonce makes every write a CHANGE, which is what fires `storage` elsewhere.
        this.#opts.storage.setItem(IDENTITY_STORAGE_KEY, JSON.stringify(message));
        routes += 1;
      }
    } catch {
      // No storage (private mode): the channel above is all there is.
    }
    if (routes === 0) {
      console.warn(`[servwell] no route told the other tabs about this ${message.kind}`);
    }
  }

  /** One message from another tab, by either route. Both routes deliver it: idempotent. */
  receive(raw: unknown): void {
    if (!isMessage(raw) || raw.tab === this.tab) return;
    if (this.#snapshot.stopped) return;
    const mine = this.#drawing;
    if (mine !== null) {
      // The same person in the same shop: not someone else, but maybe in another ROLE (a
      // demotion confirmed in that tab). Ask the server again, so this tab's own confirm
      // sees the role and empties what the old role cached before anything more is drawn.
      if (raw.kind === 'account' && raw.account === mine) {
        this.#opts.onRecheck();
        return;
      }
      this.#clearHold();
      this.#set({
        stopped:
          raw.kind === 'account' && raw.account === null ? 'signed-out' : 'other-account',
        held: false,
      });
      this.#opts.onStop();
      return;
    }
    if (raw.kind === 'transition') this.#hold(raw.at);
    else this.#release();
  }

  /** Draw no shop until the transition that started at `at` is announced or too old. */
  #hold(at: number): void {
    const remaining = at + TRANSITION_HOLD_MS - this.#timers.now();
    if (remaining <= 0) return;
    this.#clearHold();
    this.#holdTimer = this.#timers.setTimeout(() => this.#release(), remaining);
    if (!this.#snapshot.held) this.#set({ ...this.#snapshot, held: true });
  }

  #clearHold(): void {
    if (this.#holdTimer !== null) this.#timers.clearTimeout(this.#holdTimer);
    this.#holdTimer = null;
  }

  #release(): void {
    this.#clearHold();
    if (this.#snapshot.held) this.#set({ ...this.#snapshot, held: false });
    this.#opts.onRecheck();
  }
}

// ── This browser ─────────────────────────────────────────────────────────────

/** The real routes: a BroadcastChannel, localStorage and its `storage` event. */
export function browserRoutes(): TabRoutes {
  return {
    channel:
      typeof globalThis.BroadcastChannel === 'function'
        ? () => new BroadcastChannel(IDENTITY_CHANNEL) as ChannelLike
        : undefined,
    storage: safeLocalStorage(),
    listenStorage: (listener) => {
      const target = globalThis.window as Window | undefined;
      if (!target?.addEventListener) return () => {};
      const onStorage = (e: StorageEvent) => listener(e.key, e.newValue);
      target.addEventListener('storage', onStorage);
      return () => target.removeEventListener('storage', onStorage);
    },
  };
}

export function useTabsSnapshot(tabs: IdentityTabs): TabsSnapshot {
  return useSyncExternalStore(tabs.subscribe, tabs.snapshot, tabs.snapshot);
}
